import { useCallback, useEffect, useMemo } from 'react';
import { SiteInfo } from '@cbo/shared-library';
import useSiteContext from '../../../org/SiteContext';
import { useSites } from '../../../contexts/siteContext';
import { SingleSelectGroup } from '../types';

/**
 * Custom hook that provides a function to update global site context using site ID
 * Optimized with useMemo to prevent unnecessary re-renders
 */
export const useSiteContextUpdater = (updateGlobalSiteFromId: (siteId: string) => void) =>
  useCallback(
    (siteInfo: SiteInfo) => {
      if (siteInfo?.enterpriseUnitId) {
        updateGlobalSiteFromId(siteInfo.enterpriseUnitId);
      }
    },
    [updateGlobalSiteFromId]
  );

/**
 * Custom hook that creates a site change handler that updates contexts when enabled
 * Optimized with early return and Map lookup for better performance
 */
export const useSiteChangeHandler = (
  updateGlobalSiteOnChange: boolean | undefined,
  sortedSites: SiteInfo[],
  updateSiteContexts: (selectedSiteInfo: SiteInfo) => void
) => {
  // Memoize the sites lookup for better performance
  const sitesMap = useMemo(() => new Map(sortedSites.map((site) => [site.enterpriseUnitId, site])), [sortedSites]);

  return useCallback(
    (selectedSiteId: string) => {
      // Early return if feature is disabled or no site ID
      if (!updateGlobalSiteOnChange || !selectedSiteId) {
        return;
      }

      const selectedSiteInfo = sitesMap.get(selectedSiteId);
      if (selectedSiteInfo) {
        updateSiteContexts(selectedSiteInfo);
      }
    },
    [updateGlobalSiteOnChange, sitesMap, updateSiteContexts]
  );
};

/**
 * Custom hook that watches for site selection changes and automatically updates contexts
 * Optimized with better array validation
 */
export const useSiteChangeWatcher = (
  watchedSiteValue: SingleSelectGroup[],
  updateGlobalSiteOnChange: boolean | undefined,
  handleSiteChange: (siteId: string) => void
) => {
  useEffect(() => {
    // Early return if feature is disabled or no valid selection
    if (!updateGlobalSiteOnChange || !watchedSiteValue?.length) {
      return;
    }

    const selectedSite = watchedSiteValue[0];
    if (selectedSite?.id) {
      handleSiteChange(selectedSite.id);
    }
  }, [watchedSiteValue, updateGlobalSiteOnChange, handleSiteChange]);
};

/**
 * Comprehensive hook that handles all site synchronization logic
 * Optimized with proper dependency management and early returns
 */
export const useSiteSynchronization = (
  updateGlobalSiteOnChange: boolean | undefined,
  watchedSiteValue: SingleSelectGroup[]
) => {
  // Get all necessary dependencies from contexts
  const { sortedSites } = useSites();
  const { updateSelectedSiteFromId } = useSiteContext();

  // Create site context updater (memoized)
  const updateSiteContexts = useSiteContextUpdater(updateSelectedSiteFromId);

  // Create site change handler (memoized with optimized lookup)
  const handleSiteChange = useSiteChangeHandler(updateGlobalSiteOnChange, sortedSites, updateSiteContexts);

  // Watch for changes to the site selection and update global state if enabled
  useSiteChangeWatcher(watchedSiteValue, updateGlobalSiteOnChange, handleSiteChange);

  return { handleSiteChange };
};
